import React from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle2,
  FileSignature,
  ChefHat,
  Smartphone,
  Rocket,
  Phone,
  KeyRound,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useOnboarding } from 'features/onboarding/context/OnboardingContext';
import ConfettiBurst from './ConfettiBurst';

const SuccessScreen: React.FC = () => {
  const { t } = useTranslation();
  const { data, reset } = useOnboarding();
  const navigate = useNavigate();

  const registerAgain = () => {
    reset();
    navigate('/register');
  };

  const nextSteps = [
    { icon: FileSignature, key: 'success.step1' },
    { icon: ChefHat, key: 'success.step2' },
    { icon: Smartphone, key: 'success.step3' },
    { icon: Rocket, key: 'success.step4' },
  ];

  return (
    <motion.section
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="card relative overflow-hidden p-6 sm:p-10 text-center"
    >
      <ConfettiBurst />
      <div className="relative z-10">
        <div className="flex justify-center mb-5">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 border-4 border-emerald-100"
          >
            <CheckCircle2 size={42} strokeWidth={2.5} />
          </motion.div>
        </div>

        <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900">
          {t('success.title')}
        </h1>
        <p className="mt-3 text-base text-gray-600 max-w-lg mx-auto leading-relaxed">
          {t('success.message')}
        </p>

        <div className="mt-8 max-w-2xl mx-auto">
          <div className="rounded-2xl border-2 border-haat-red-100 bg-haat-red-50/60 p-5 text-center">
            <p className="text-[11px] font-bold uppercase tracking-wider text-haat-red-600">
              {t('success.submissionId')}
            </p>
            <p className="mt-1.5 text-2xl sm:text-3xl font-mono font-extrabold tracking-wide text-haat-red-700 break-all">
              {data.metadata.submissionId || '-'}
            </p>
            <div className="mt-3 flex items-start gap-2 justify-center text-start">
              <KeyRound
                size={16}
                className="text-amber-500 flex-shrink-0 mt-0.5"
              />
              <p className="text-xs text-gray-600 leading-relaxed max-w-md">
                {t('success.saveIdNote')}
              </p>
            </div>
          </div>

          <div className="mt-3 grid sm:grid-cols-2 gap-3 text-start">
            <div className="rounded-xl bg-gray-50 border border-gray-100 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                {t('success.businessName')}
              </p>
              <p className="mt-0.5 text-sm font-semibold text-gray-900 break-words">
                {data.businessInfo.commercialName || '-'}
              </p>
            </div>
            <div className="rounded-xl bg-gray-50 border border-gray-100 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                {t('success.email')}
              </p>
              <p className="mt-0.5 text-sm font-semibold text-gray-900 break-all">
                {data.businessInfo.email || '-'}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-10 text-start max-w-2xl mx-auto">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 text-center sm:text-start">
            {t('success.nextStepsTitle')}
          </h3>
          <ol className="flex items-start justify-between gap-1">
            {nextSteps.map(({ icon: Icon, key }, idx) => {
              const isLast = idx === nextSteps.length - 1;
              return (
                <motion.li
                  key={key}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    delay: 0.3 + idx * 0.08,
                    duration: 0.35,
                    ease: 'easeOut',
                  }}
                  className="relative flex-1 flex flex-col items-center text-center"
                >
                  {!isLast && (
                    <span
                      aria-hidden
                      className="absolute top-5 h-px bg-gray-200"
                      style={{
                        insetInlineEnd: '-50%',
                        insetInlineStart: '50%',
                      }}
                    />
                  )}
                  <span className="relative z-10 w-10 h-10 rounded-full bg-haat-red-50 text-haat-red-600 flex items-center justify-center ring-4 ring-white">
                    <Icon size={18} />
                  </span>
                  <p className="mt-2 text-[10px] font-semibold text-gray-400">
                    {String(idx + 1).padStart(2, '0')}
                  </p>
                  <p className="mt-0.5 text-[11px] leading-tight font-medium text-gray-800 px-0.5">
                    {t(key)}
                  </p>
                </motion.li>
              );
            })}
          </ol>
        </div>

        <div className="mt-10 max-w-2xl mx-auto rounded-2xl border border-gray-100 bg-gray-50 p-5 text-start">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-xl bg-haat-red-50 text-haat-red-600 flex items-center justify-center flex-shrink-0">
                <Phone size={20} />
              </span>
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {t('success.supportTitle')}
                </p>
                <p className="text-xs text-gray-600 mt-0.5">
                  {t('success.supportSubtitle')}
                </p>
              </div>
            </div>
            <a
              href="tel:+97246000905"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-white border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-100 transition-colors"
              dir="ltr"
            >
              <Phone size={16} />
              04-600-0905
            </a>
          </div>
        </div>

        <div className="mt-8 flex flex-col items-center gap-4">
          <button
            type="button"
            onClick={registerAgain}
            className="btn-haat inline-flex items-center justify-center"
          >
            {t('success.registerAgain')}
          </button>
          <p className="text-sm text-gray-600 max-w-md mx-auto">
            {t('success.trackStatusHint')}{' '}
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="font-semibold text-haat-red-600 hover:text-haat-red-700 underline underline-offset-2"
            >
              {t('success.trackStatusCta')}
            </button>
          </p>
        </div>
      </div>
    </motion.section>
  );
};

export default SuccessScreen;
